import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    ArrowForward as BackIcon,
} from '@mui/icons-material';
import useFetch from '../../../../hooks/useFetch';
import useDeleteItem from '../../../../hooks/useDeleteItem';
import usePostData from '../../../../hooks/usePostData';
import RTL from '../../../../utils/RTL';
import { toPersianDigits } from '../../../../utils/convertNumbers';
import { formatJalaliDateTime } from '../../../../utils/formatJalaliDate';
import { API } from '../../../../utils/apiRoutes';
import StandardTable from '../../../common/table/StandardTable';
import ConfirmationDialog from '../ConfirmationDialog';
import FloatingSnackbar from '../../../common/FloatingSnackbar';
import api from '../../../../config/axiosConfig';

function PersonalNotes() {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [formOpen, setFormOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [noteToDelete, setNoteToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [formData, setFormData] = useState({ title: '', content: '', tags: [] });
    const [tagInput, setTagInput] = useState('');

    const { deleteItem, isLoading: isDeleting } = useDeleteItem();
    const [postData, isCreating] = usePostData(API.personalNotes);
    const [isUpdating, setIsUpdating] = useState(false);

    // Debounce search
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Build API params
    const apiParams = useMemo(() => {
        const params = {};
        if (debouncedSearch) {
            params.search = debouncedSearch;
        }
        return params;
    }, [debouncedSearch]);

    const { data, loading, error, refetch } = useFetch(API.personalNotes, {
        params: apiParams,
        skip: false,
    });

    const notes = data?.results || data || [];
    const totalCount = data?.count || notes.length;

    const handleCreate = useCallback(() => {
        setSelectedNote(null);
        setFormData({ title: '', content: '', tags: [] });
        setTagInput('');
        setFormOpen(true);
    }, []);

    const handleEdit = useCallback((note) => {
        setSelectedNote(note);
        setFormData({
            title: note.title || '',
            content: note.content || '',
            tags: note.tags || [],
        });
        setTagInput('');
        setFormOpen(true);
    }, []);

    const handleDelete = useCallback((note) => {
        setNoteToDelete(note);
        setDeleteDialogOpen(true);
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!noteToDelete) return;
        try {
            await deleteItem(`${API.personalNotes}${noteToDelete.id}/`);
            setSnackbar({
                open: true,
                message: 'یادداشت با موفقیت حذف شد.',
                severity: 'success',
            });
            refetch();
        } catch (err) {
            setSnackbar({
                open: true,
                message: err?.response?.data?.detail || 'خطا در حذف یادداشت',
                severity: 'error',
            });
        } finally {
            setDeleteDialogOpen(false);
            setNoteToDelete(null);
        }
    }, [noteToDelete, deleteItem, refetch]);

    const handleFormChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleAddTag = useCallback(() => {
        if (tagInput.trim()) {
            setFormData(prev => ({
                ...prev,
                tags: [...(prev.tags || []), tagInput.trim()],
            }));
            setTagInput('');
        }
    }, [tagInput]);

    const handleRemoveTag = useCallback((index) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter((_, i) => i !== index),
        }));
    }, []);

    const handleCloseForm = useCallback(() => {
        setFormOpen(false);
        setSelectedNote(null);
        setFormData({ title: '', content: '', tags: [] });
        setTagInput('');
    }, []);

    const handleFormSubmit = useCallback(async () => {
        try {
            const payload = {
                title: formData.title || null,
                content: formData.content,
                tags: formData.tags || [],
            };

            if (selectedNote) {
                // Update existing note
                setIsUpdating(true);
                await api.patch(`${API.personalNotes}${selectedNote.id}/`, payload);
                setSnackbar({
                    open: true,
                    message: 'یادداشت با موفقیت به‌روزرسانی شد.',
                    severity: 'success',
                });
            } else {
                // Create new note
                const result = await postData(payload);
                if (result && result.success) {
                    setSnackbar({
                        open: true,
                        message: 'یادداشت با موفقیت ایجاد شد.',
                        severity: 'success',
                    });
                } else {
                    const errorMsg = result?.error || 'خطا در ایجاد یادداشت';
                    throw new Error(errorMsg);
                }
            }
            handleCloseForm();
            refetch();
        } catch (err) {
            setSnackbar({
                open: true,
                message: err?.response?.data?.detail || err?.message || 'خطا در ذخیره یادداشت',
                severity: 'error',
            });
        } finally {
            setIsUpdating(false);
        }
    }, [formData, selectedNote, postData, refetch, handleCloseForm]);

    // Table configuration
    const columns = useMemo(() => [
        {
            id: 'title',
            label: 'عنوان',
            align: 'start',
            render: (row) => (
                <Typography variant="body2" sx={{ fontWeight: row.title ? 'medium' : 'normal' }}>
                    {row.title || 'بدون عنوان'}
                </Typography>
            ),
        },
        {
            id: 'content',
            label: 'محتوا',
            align: 'start',
            render: (row) => (
                <Typography
                    variant="body2"
                    sx={{
                        maxWidth: '400px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {row.content}
                </Typography>
            ),
        },
        {
            id: 'tags',
            label: 'برچسب‌ها',
            align: 'center',
            priority: 'secondary',
            render: (row) => (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                    {row.tags && row.tags.length > 0 ? (
                        row.tags.slice(0, 3).map((tag, idx) => (
                            <Chip key={idx} label={tag} size="small" />
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                </Box>
            ),
        },
        {
            id: 'updated_at',
            label: 'تاریخ و\nساعت',
            align: 'center',
            priority: 'secondary',
            render: (row) => {
                if (!row.updated_at) {
                    return <Typography variant="body2" color="text.secondary">—</Typography>;
                }
                const formattedDateTime = formatJalaliDateTime(row.updated_at);
                return (
                    <Typography variant="body2">
                        {formattedDateTime ? toPersianDigits(formattedDateTime) : '—'}
                    </Typography>
                );
            },
        },
        {
            id: 'actions',
            label: 'عملیات',
            compact: true,
            align: 'center',
            render: (row) => (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(row);
                        }}
                        color="primary"
                        aria-label="ویرایش"
                        title="ویرایش"
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(row);
                        }}
                        aria-label="حذف"
                        title="حذف"
                        color="error"
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ], [handleEdit, handleDelete]);

    return (
        <RTL>
            <Box sx={{ p: 3 }}>
                {formOpen ? (
                    /* Full-page create/edit form (drill-down) */
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                            <Button startIcon={<BackIcon />} variant="outlined" onClick={handleCloseForm}>
                                بازگشت به فهرست
                            </Button>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                {selectedNote ? 'ویرایش یادداشت' : 'ایجاد یادداشت جدید'}
                            </Typography>
                        </Box>
                        <Paper sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="عنوان (اختیاری)"
                                    value={formData.title}
                                    onChange={(e) => handleFormChange('title', e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                />
                                <TextField
                                    label="محتوا"
                                    value={formData.content}
                                    onChange={(e) => handleFormChange('content', e.target.value)}
                                    fullWidth
                                    multiline
                                    rows={8}
                                    variant="outlined"
                                    required
                                />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        برچسب‌ها
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                        {formData.tags && formData.tags.map((tag, index) => (
                                            <Chip
                                                key={index}
                                                label={tag}
                                                onDelete={() => handleRemoveTag(index)}
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <TextField
                                            size="small"
                                            placeholder="افزودن برچسب..."
                                            value={tagInput}
                                            onChange={(e) => setTagInput(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddTag();
                                                }
                                            }}
                                            sx={{ flexGrow: 1 }}
                                        />
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={handleAddTag}
                                        >
                                            افزودن
                                        </Button>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                                    <Button onClick={handleCloseForm}>انصراف</Button>
                                    <Button
                                        onClick={handleFormSubmit}
                                        variant="contained"
                                        disabled={!formData.content.trim() || isCreating || isUpdating}
                                    >
                                        {isCreating || isUpdating ? (
                                            <>
                                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                                در حال ذخیره...
                                            </>
                                        ) : (
                                            'ذخیره'
                                        )}
                                    </Button>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                ) : (
                    <Paper sx={{ p: 3 }}>
                        {/* Page Title */}
                        <Box sx={{ mb: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                یادداشت‌های شخصی
                            </Typography>
                        </Box>
                        {/* Header with search and create button */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
                            <TextField
                                size="small"
                                placeholder="جستجو در عنوان و محتوا..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ ml: 1, color: 'text.secondary' }} />,
                                }}
                                sx={{ flexGrow: 1 }}
                            />
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleCreate}
                                sx={{ minWidth: '120px' }}
                            >
                                یادداشت جدید
                            </Button>
                        </Box>

                        {/* Notes table */}
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : error ? (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error?.message || 'خطا در بارگذاری یادداشت‌ها'}
                            </Alert>
                        ) : notes.length === 0 ? (
                            <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                {searchTerm ? 'یادداشتی یافت نشد.' : 'هنوز یادداشتی ایجاد نکرده‌اید.'}
                            </Alert>
                        ) : (
                            <StandardTable
                                columns={columns}
                                rows={notes}
                                onRowClick={handleEdit}
                                getRowKey={(row) => row.id}
                            />
                        )}

                        {totalCount > 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                                تعداد کل: {toPersianDigits(totalCount.toString())}
                            </Typography>
                        )}
                    </Paper>
                )}

                {/* Delete Confirmation Dialog */}
                <ConfirmationDialog
                    open={deleteDialogOpen}
                    onClose={() => {
                        setDeleteDialogOpen(false);
                        setNoteToDelete(null);
                    }}
                    onConfirm={confirmDelete}
                    title="تأیید حذف"
                    message="آیا مطمئن هستید که می‌خواهید این یادداشت را حذف کنید؟"
                    confirmColor="error"
                    isLoading={isDeleting}
                />

                {/* Snackbar for notifications */}
                <FloatingSnackbar
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                />
            </Box>
        </RTL>
    );
}

export default PersonalNotes;
